import React from 'react';
import { PhotoIcon, VideoCameraIcon, XMarkIcon, FunnelIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutline } from '@heroicons/react/24/outline';

interface FilterChipsProps {
  isFavoriteSelected: boolean;
  isMediaTypeImageSelected: boolean;
  isMediaTypeVideoSelected: boolean;
  onToggleFavorite: () => void;
  onToggleMediaTypeImage: () => void;
  onToggleMediaTypeVideo: () => void;
  onClearFilters: () => void;
}

const FilterChips: React.FC<FilterChipsProps> = ({
   isFavoriteSelected,
   isMediaTypeImageSelected,
   isMediaTypeVideoSelected,
   onToggleFavorite,
   onToggleMediaTypeImage,
   onToggleMediaTypeVideo,
   onClearFilters,
 }) => {
  const activeFiltersCount = [
    isFavoriteSelected,
    isMediaTypeImageSelected,
    isMediaTypeVideoSelected,
  ].filter(Boolean).length;

  return (
    <div className="relative w-full">
      {/* Mobile Filters */}
      <div className="sm:hidden">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters</span>
          </div>
          {activeFiltersCount > 0 && (
            <button
              onClick={onClearFilters}
              className="text-sm text-indigo-600 hover:text-indigo-700"
            >
              Clear all ({activeFiltersCount})
            </button>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          <button
            onClick={onToggleFavorite}
            className={`flex-none inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors touch-manipulation
              ${isFavoriteSelected
              ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-200'
              : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200'}`}
          >
            <StarOutline
              className={`h-4 w-4 mr-1.5 ${isFavoriteSelected ? 'text-indigo-700' : 'text-gray-500'}`}
            />
            Favorites
          </button>

          <button
            onClick={onToggleMediaTypeImage}
            className={`flex-none inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors touch-manipulation
              ${isMediaTypeImageSelected
              ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-200'
              : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200'}`}
          >
            <PhotoIcon
              className={`h-4 w-4 mr-1.5 ${isMediaTypeImageSelected ? 'text-indigo-700' : 'text-gray-500'}`}
            />
            Images
          </button>

          <button
            onClick={onToggleMediaTypeVideo}
            className={`flex-none inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors touch-manipulation
              ${isMediaTypeVideoSelected
              ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-200'
              : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200'}`}
          >
            <VideoCameraIcon
              className={`h-4 w-4 mr-1.5 ${isMediaTypeVideoSelected ? 'text-indigo-700' : 'text-gray-500'}`}
            />
            Videos
          </button>
        </div>
      </div>

      {/* Desktop Filters */}
      <div className="hidden sm:block">
        <div className="flex flex-wrap gap-3 mb-4">
          <button
            onClick={onToggleFavorite}
            className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors
              ${isFavoriteSelected
              ? 'bg-indigo-100/80 text-indigo-700'
              : 'bg-gray-100/80 text-gray-700 hover:bg-gray-200/80'}`}
          >
            <StarOutline
              className={`h-4 w-4 mr-2 ${isFavoriteSelected ? 'text-indigo-700' : 'text-gray-500'}`}
            />
            Favorites
          </button>

          <button
            onClick={onToggleMediaTypeImage}
            className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors
              ${isMediaTypeImageSelected
              ? 'bg-indigo-100/80 text-indigo-700'
              : 'bg-gray-100/80 text-gray-700 hover:bg-gray-200/80'}`}
          >
            <PhotoIcon
              className={`h-4 w-4 mr-2 ${isMediaTypeImageSelected ? 'text-indigo-700' : 'text-gray-500'}`}
            />
            Images
          </button>

          <button
            onClick={onToggleMediaTypeVideo}
            className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors
              ${isMediaTypeVideoSelected
              ? 'bg-indigo-100/80 text-indigo-700'
              : 'bg-gray-100/80 text-gray-700 hover:bg-gray-200/80'}`}
          >
            <VideoCameraIcon
              className={`h-4 w-4 mr-2 ${isMediaTypeVideoSelected ? 'text-indigo-700' : 'text-gray-500'}`}
            />
            Videos
          </button>

          {activeFiltersCount > 0 && (
            <button
              onClick={onClearFilters}
              className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-gray-100/80 text-gray-700 hover:bg-gray-200/80 transition-colors"
            >
              <XMarkIcon className="h-4 w-4 mr-1" />
              Clear ({activeFiltersCount})
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterChips;